/**
 * Financeiro: Conciliação (por pedido), Resultados (por produto, marca, categoria) e Despesas (lançadas pelo cliente).
 * Resultado = líquido do canal − custo dos produtos (na data da venda) − despesas. Impostos ficam de fora.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import * as Dialog from '@radix-ui/react-dialog';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Download, Pencil, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { downloadCsv } from '../csv.ts';
import { api, brl, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { can } from '@berytus/contracts';

const CHANNEL: Record<string, string> = { mercadolivre: 'Mercado Livre', shopee: 'Shopee', ifood: 'iFood' };
const TZ = 'America/Sao_Paulo';
const ymd = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d);
const pctOf = (v: number | null | undefined) => (v == null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`);
const share = (part: number, whole: number) => (whole ? `${((part / whole) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : '');
const money = (v: number) => <span style={{ color: v < 0 ? 'var(--err)' : undefined }}>{brl(v)}</span>;

function presets() {
  const now = new Date();
  const today = ymd(now);
  const back = (d: number) => ymd(new Date(now.getTime() - d * 86_400_000));
  const [y, m] = today.split('-').map(Number) as [number, number];
  const first = `${y}-${String(m).padStart(2, '0')}-01`;
  const pm = m === 1 ? 12 : m - 1, py = m === 1 ? y - 1 : y;
  const prevFirst = `${py}-${String(pm).padStart(2, '0')}-01`;
  const prevLast = ymd(new Date(new Date(`${first}T12:00:00Z`).getTime() - 86_400_000));
  return [
    { key: 'today', label: 'Hoje', from: today, to: today },
    { key: '7d', label: '7 dias', from: back(6), to: today },
    { key: '30d', label: '30 dias', from: back(29), to: today },
    { key: 'month', label: 'Este mês', from: first, to: today },
    { key: 'prev', label: 'Mês passado', from: prevFirst, to: prevLast },
  ];
}

interface Account { id: string; channel: string; name: string | null }

/** Filtros na URL (dá para compartilhar o link): período, canal, conta e busca. */
function useFinanceFilter() {
  const [params, setParams] = useSearchParams();
  const ps = presets();
  const from = params.get('de') ?? ps[3]!.from;
  const to = params.get('ate') ?? ps[3]!.to;
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v ? p.set(k, v) : p.delete(k));
    setParams(p, { replace: true });
  };
  const query = new URLSearchParams({ from, to });
  for (const [k, q] of [['canal', 'channel'], ['conta', 'accountId'], ['busca', 'q']] as const) if (params.get(k)) query.set(q, params.get(k)!);
  return { params, set, from, to, query, presets: ps };
}

function FilterBar({ f, accounts, extra }: { f: ReturnType<typeof useFinanceFilter>; accounts: Account[]; extra?: ReactNode }) {
  const [search, setSearch] = useState(f.params.get('busca') ?? '');
  const active = f.presets.find((p) => p.from === f.from && p.to === f.to)?.key;
  const channel = f.params.get('canal') ?? '';
  return (
    <div className="toolbar" style={{ flexWrap: 'wrap' }}>
      <div className="seg" role="group" aria-label="Período">
        {f.presets.map((p) => (
          <button key={p.key} type="button" aria-pressed={active === p.key} onClick={() => f.set({ de: p.from, ate: p.to, pagina: null })}>{p.label}</button>
        ))}
      </div>
      <input className="input" type="date" aria-label="De" value={f.from} max={f.to} onChange={(e) => e.target.value && f.set({ de: e.target.value, pagina: null })} style={{ width: 150 }} />
      <input className="input" type="date" aria-label="Até" value={f.to} min={f.from} onChange={(e) => e.target.value && f.set({ ate: e.target.value, pagina: null })} style={{ width: 150 }} />
      <select className="select" aria-label="Canal" value={channel} onChange={(e) => f.set({ canal: e.target.value || null, conta: null, pagina: null })}>
        <option value="">Todos os canais</option>
        {[...new Set(accounts.map((a) => a.channel))].map((c) => <option key={c} value={c}>{CHANNEL[c] ?? c}</option>)}
      </select>
      {accounts.filter((a) => !channel || a.channel === channel).length > 1 && (
        <select className="select" aria-label="Conta" value={f.params.get('conta') ?? ''} onChange={(e) => f.set({ conta: e.target.value || null, pagina: null })}>
          <option value="">Todas as contas</option>
          {accounts.filter((a) => !channel || a.channel === channel).map((a) => <option key={a.id} value={a.id}>{CHANNEL[a.channel] ?? a.channel} · {a.name}</option>)}
        </select>
      )}
      <form className="search" onSubmit={(e) => { e.preventDefault(); f.set({ busca: search.trim() || null, pagina: null }); }}>
        <Search size={16} />
        <input className="input" type="search" placeholder="Pedido, SKU ou produto" value={search} onChange={(e) => { setSearch(e.target.value); if (!e.target.value) f.set({ busca: null }); }} />
      </form>
      {extra}
    </div>
  );
}

// ---------------------------------------------------------------- Conciliação

interface Totals {
  orders: number; units: number; gross: number; sellerDiscount: number; channelDiscount: number; commission: number; serviceFee: number;
  shipping: number; refund: number; otherFees: number; net: number; cost: number; expenses: number; result: number; sold: number;
}
interface OrderRow {
  id: string; externalId: string; channel: string; accountName: string | null; createdAt: string; status: string; units: number;
  hasFinance: boolean; final: boolean; error: string | null; gross: number; sellerDiscount: number; channelDiscount: number; fees: number;
  shipping: number; net: number; cost: number; expenses: number; result: number | null; margin: number | null; noCost: number; currentCost: boolean; refund: number;
}
interface OrdersResponse {
  summary: Totals; byChannel: (Totals & { channel: string })[]; expenses: { id: string; name: string; amount: number }[]; pending: number;
  counts: Record<string, number>; accounts: Account[]; total: number; page: number; pageSize: number; items: OrderRow[];
}

const VIEWS: [string, string][] = [['all', 'Todos'], ['attention', 'Atenção'], ['loss', 'Prejuízo'], ['no_cost', 'Sem custo'], ['pending', 'Aguardando extrato']];

export function FinanceReconciliation() {
  const f = useFinanceFilter();
  const qc = useQueryClient();
  const view = f.params.get('ver') ?? 'all';
  const page = Number(f.params.get('pagina') ?? 1);
  const sort = f.params.get('ordem') ?? 'date';
  const q = new URLSearchParams(f.query);
  q.set('view', view); q.set('page', String(page)); q.set('sort', sort); q.set('dir', f.params.get('dir') ?? 'desc');
  const data = useQuery({ queryKey: ['finance-orders', q.toString()], queryFn: () => api<OrdersResponse>(`/finance/orders?${q}`), placeholderData: keepPreviousData });
  const refresh = useMutation({
    mutationFn: () => api('/finance/refresh', { method: 'POST', json: {} }),
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['finance-orders'] }), 4000),
  });
  const reprocess = useMutation({ mutationFn: () => api<{ orders: number }>('/finance/refresh', { method: 'POST', json: { all: true } }) });
  const [open, setOpen] = useState<string | null>(null);
  const d = data.data;
  const s = d?.summary;
  const sortBy = (k: string) => f.set({ ordem: k, dir: sort === k && f.params.get('dir') !== 'asc' ? 'asc' : null, pagina: null });
  const th = (k: string, label: string) => (
    <th className="num" style={{ cursor: 'pointer' }} onClick={() => sortBy(k)}>{label}{sort === k ? (f.params.get('dir') === 'asc' ? ' ↑' : ' ↓') : ''}</th>
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Conciliação</h1>
          <p>Quanto cada venda deixou: descontos de cada lado, comissão e taxas do canal, frete, custo dos produtos (na data da venda) e as suas despesas. Impostos ficam de fora.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" type="button" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
            <RefreshCw size={16} /> {refresh.isSuccess ? 'Atualizando…' : 'Atualizar extratos'}
          </button>
          <button className="btn quiet" type="button" disabled={reprocess.isPending || reprocess.isSuccess}
            title="Relê no canal o extrato de todos os pedidos dos últimos 120 dias (aos poucos, em segundo plano)"
            onClick={() => window.confirm('Reler o extrato de todos os pedidos dos últimos 120 dias? Roda em segundo plano, aos poucos.') && reprocess.mutate()}>
            {reprocess.isSuccess ? `Reprocessando ${int(reprocess.data.orders)} pedidos…` : 'Reprocessar todos'}
          </button>
        </div>
      </div>
      <FilterBar f={f} accounts={d?.accounts ?? []} />
      {data.error ? <ErrorBox error={data.error} /> : !d || !s ? <Loading /> : (
        <div className="stack-y">
          <div className="cols" style={{ gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', gap: 'var(--gap)', alignItems: 'start' }}>
            <section className="panel stack-y" style={{ gap: 6 }}>
              <h2 style={{ margin: '0 0 6px' }}>De onde vem e para onde vai</h2>
              <Waterfall s={s} expenses={d.expenses} />
              {d.pending > 0 && <p className="small muted" style={{ margin: '6px 0 0' }}>{int(d.pending)} pedido(s) ainda sem extrato do canal não entram nos valores (veja "Aguardando extrato").</p>}
            </section>
            <div className="stack-y">
              <div className="kpis" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                <div className="kpi"><span className="k-label">Vendido</span><span className="k-value">{brl(s.sold)}</span><span className="k-note">{int(s.orders)} pedidos · {int(s.units)} unidades</span></div>
                <div className={`kpi${s.result < 0 ? ' alert' : ''}`}><span className="k-label">Resultado</span><span className="k-value">{brl(s.result)}</span><span className="k-note">margem {share(s.result, s.sold) || '—'}</span></div>
                <div className="kpi"><span className="k-label">Líquido dos canais</span><span className="k-value">{brl(s.net)}</span><span className="k-note">{share(s.net, s.sold)} do vendido</span></div>
                <div className="kpi"><span className="k-label">Pago pelos canais</span><span className="k-value">{brl(s.channelDiscount)}</span><span className="k-note">cupons e subsídios: não saem do seu bolso</span></div>
              </div>
            </div>
          </div>

          {d.byChannel.length > 0 && (
                <section className="panel flush" style={{ overflowX: 'auto' }}>
                  <table className="grid" style={{ width: '100%' }}>
                    <thead><tr><th>Canal</th><th className="num">Vendido</th><th className="num">Pago pelo canal</th><th className="num">Taxas</th><th className="num">Frete</th><th className="num">Líquido</th><th className="num">Custo</th><th className="num">Despesas</th><th className="num">Resultado</th><th className="num">Margem</th></tr></thead>
                    <tbody>
                      {d.byChannel.map((c) => (
                        <tr key={c.channel}>
                          <td>{CHANNEL[c.channel] ?? c.channel}<div className="small muted">{int(c.orders)} pedidos</div></td>
                          <td className="num">{brl(c.sold)}</td>
                          <td className="num">{c.channelDiscount ? brl(c.channelDiscount) : '—'}</td>
                          <td className="num">{brl(c.commission + c.serviceFee + c.otherFees + c.refund)}<div className="small muted">{share(c.commission + c.serviceFee + c.otherFees + c.refund, c.sold)}</div></td>
                          <td className="num">{brl(c.shipping)}<div className="small muted">{share(c.shipping, c.sold)}</div></td>
                          <td className="num">{brl(c.net)}</td>
                          <td className="num">{brl(c.cost)}</td>
                          <td className="num">{brl(c.expenses)}</td>
                          <td className="num"><b>{money(c.result)}</b></td>
                          <td className="num">{share(c.result, c.sold) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

          <section className="panel flush">
            <div style={{ padding: '12px 14px 0' }}>
              <div className="seg" role="tablist" aria-label="Pedidos">
                {VIEWS.map(([k, l]) => (
                  <button key={k} type="button" role="tab" aria-pressed={view === k} onClick={() => f.set({ ver: k === 'all' ? null : k, pagina: null })}>
                    {l} {d.counts[k] != null && <span className="muted">({int(d.counts[k]!)})</span>}
                  </button>
                ))}
              </div>
            </div>
            {d.items.length === 0 ? <div className="empty"><b>Nenhum pedido</b>Mude o período ou os filtros.</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table className="grid" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => sortBy('date')}>Data{sort === 'date' ? (f.params.get('dir') === 'asc' ? ' ↑' : ' ↓') : ''}</th>
                      <th>Pedido</th>{th('sold', 'Vendido')}<th className="num">Pago pelo canal</th><th className="num">Taxas</th><th className="num">Frete</th>
                      {th('net', 'Líquido')}<th className="num">Custo</th><th className="num">Despesas</th>{th('result', 'Resultado')}{th('margin', 'Margem')}
                    </tr>
                  </thead>
                  <tbody>
                    {d.items.map((o) => (
                      <tr key={o.id} onClick={() => setOpen(o.id)} style={{ cursor: 'pointer' }}>
                        <td className="small">{dateTime(o.createdAt)}</td>
                        <td>
                          <span className="code">{o.externalId}</span>
                          <div className="small muted">{CHANNEL[o.channel] ?? o.channel}{o.accountName ? ` · ${o.accountName}` : ''} · {int(o.units)} un.</div>
                          {!o.hasFinance && <Tag tone={o.error ? 'err' : 'off'}>{o.error ? 'Erro no extrato' : 'Aguardando extrato'}</Tag>}
                          {o.noCost > 0 && <> <Tag tone="warn">Sem custo</Tag></>}
                          {o.refund > 0 && <> <Tag tone="warn">Devolução</Tag></>}
                        </td>
                        <td className="num">{o.hasFinance ? brl(o.gross - o.sellerDiscount) : '—'}{o.sellerDiscount > 0 && <div className="small muted">desc. loja {brl(o.sellerDiscount)}</div>}</td>
                        <td className="num">{o.channelDiscount ? brl(o.channelDiscount) : '—'}</td>
                        <td className="num">{o.hasFinance ? brl(o.fees) : '—'}</td>
                        <td className="num">{o.hasFinance ? brl(o.shipping) : '—'}</td>
                        <td className="num">{o.hasFinance ? brl(o.net) : '—'}</td>
                        <td className="num">{brl(o.cost)}{o.currentCost && <div className="small muted" title="Pedido antigo: custo do dia do cálculo">custo atual</div>}</td>
                        <td className="num">{o.expenses ? brl(o.expenses) : '—'}</td>
                        <td className="num"><b>{o.result == null ? '—' : money(o.result)}</b></td>
                        <td className="num">{pctOf(o.margin)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pager page={d.page} pageSize={d.pageSize} total={d.total} go={(p) => f.set({ pagina: p > 1 ? String(p) : null })} />
          </section>
        </div>
      )}
      <OrderStatement id={open} onClose={() => setOpen(null)} />
    </>
  );
}

function Pager({ page, pageSize, total, go, noun = 'pedidos' }: { page: number; pageSize: number; total: number; go: (p: number) => void; noun?: string }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', padding: 12 }}>
      <span className="small muted">{int(total)} {noun} · página {page} de {pages}</span>
      <button className="btn sm" type="button" disabled={page <= 1} onClick={() => go(page - 1)} aria-label="Página anterior"><ChevronLeft size={16} /></button>
      <button className="btn sm" type="button" disabled={page >= pages} onClick={() => go(page + 1)} aria-label="Próxima página"><ChevronRight size={16} /></button>
    </div>
  );
}

function Waterfall({ s, expenses }: { s: Totals; expenses: { id: string; name: string; amount: number }[] }) {
  const fees = s.commission + s.serviceFee;
  const rows: [string, number, 'in' | 'out' | 'total', string?][] = [
    ['Produtos a preço cheio', s.gross, 'in'],
    ['Descontos pagos pela loja (cupons, campanhas)', -s.sellerDiscount, 'out'],
    ['Vendido', s.sold, 'total'],
    ['Comissão e taxas dos canais', -fees, 'out'],
    ['Frete pago pela loja', -s.shipping, 'out'],
    ['Devoluções e reembolsos', -s.refund, 'out'],
    ['Outros ajustes dos canais', -s.otherFees, 'out'],
    ['Líquido dos canais', s.net, 'total'],
    ['Custo dos produtos', -s.cost, 'out'],
    ...expenses.map((e) => [e.name, -e.amount, 'out', 'despesa'] as [string, number, 'out', string]),
    ['Resultado', s.result, 'total'],
  ];
  return (
    <table className="grid" style={{ width: '100%' }}>
      <tbody>
        {rows.filter(([, v, kind]) => kind === 'total' || Math.abs(v) >= 0.005).map(([label, v, kind, tag]) => (
          <tr key={label} style={kind === 'total' ? { fontWeight: 600, background: 'var(--surface-2)' } : undefined}>
            <td>{kind === 'out' ? '− ' : kind === 'total' ? '= ' : ''}{label}{tag && <span className="small muted"> · {tag}</span>}</td>
            <td className="num">{money(v)}</td>
            <td className="num small muted" style={{ width: 70 }}>{label === 'Produtos a preço cheio' ? '' : share(Math.abs(v), s.sold)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface Statement {
  id: string; externalId: string; packId: string | null; channel: string; accountName: string | null; status: string; createdAt: string;
  hasFinance: boolean; final: boolean; error: string | null; source: string | null; fetchedAt: string | null;
  lines: { label: string; amount: number; group: string }[];
  totals: { gross: number; sellerDiscount: number; channelDiscount: number; commission: number; serviceFee: number; shipping: number; refund: number; otherFees: number; net: number; cost: number; expenses: number; result: number | null };
  expenses: { id: string; name: string; amount: number }[];
  items: { id: string; title: string | null; sku: string | null; quantity: number; unitPrice: number | null; unitCost: number | null; costSource: string | null; gross: number; sellerDiscount: number; channelDiscount: number; fees: number; shipping: number; net: number }[];
}

function OrderStatement({ id, onClose }: { id: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['finance-order', id], queryFn: () => api<Statement>(`/finance/orders/${id}`), enabled: !!id });
  const refresh = useMutation({
    mutationFn: () => api('/finance/refresh', { method: 'POST', json: { orderIds: [id] } }),
    onSuccess: () => setTimeout(() => { qc.invalidateQueries({ queryKey: ['finance-order', id] }); qc.invalidateQueries({ queryKey: ['finance-orders'] }); }, 4000),
  });
  const o = q.data;
  return (
    <Dialog.Root open={!!id} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" aria-describedby={undefined}
          style={{ position: 'fixed', top: '5vh', left: '50%', transform: 'translateX(-50%)', width: 'min(820px, calc(100vw - 32px))', maxHeight: '90vh', overflow: 'auto', zIndex: 60, display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Dialog.Title asChild><h2 style={{ margin: 0 }}>Extrato do pedido {o?.externalId ?? ''}</h2></Dialog.Title>
            {o && (o.final ? <Tag tone="ok">Definitivo</Tag> : <Tag tone="off">Previsto</Tag>)}
            <span style={{ flex: 1 }} />
            <button className="btn sm" type="button" disabled={refresh.isPending} onClick={() => refresh.mutate()}><RefreshCw size={14} /> {refresh.isSuccess ? 'Atualizando…' : 'Ler de novo'}</button>
            <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
          </div>
          {q.isPending ? <Loading /> : q.error ? <ErrorBox error={q.error} /> : o && (
            <>
              <div className="small muted">
                {CHANNEL[o.channel] ?? o.channel}{o.accountName ? ` · ${o.accountName}` : ''} · {dateTime(o.createdAt)}{o.packId ? ` · carrinho ${o.packId}` : ''}
                {o.fetchedAt && <> · extrato lido em {dateTime(o.fetchedAt)}</>}
                {!o.final && o.hasFinance && <> · valores previstos até o pedido ser concluído</>}
              </div>
              {o.error && <div className="error" role="alert">Não consegui ler o extrato no canal: {o.error}</div>}
              {!o.hasFinance && !o.error && <p className="small muted" style={{ margin: 0 }}>O extrato deste pedido ainda não foi lido no canal (é lido a cada 10 minutos).</p>}
              {o.hasFinance && (
                <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, alignItems: 'start' }}>
                  <section className="stack-y" style={{ gap: 6 }}>
                    <h3 style={{ margin: 0 }}>Como o canal informou</h3>
                    <table className="grid" style={{ width: '100%' }}>
                      <tbody>
                        {o.lines.map((l, i) => (
                          <tr key={i} style={l.group === 'net' ? { fontWeight: 600, background: 'var(--surface-2)' } : l.group === 'info' ? { color: 'var(--muted)', fontStyle: 'italic' } : undefined}>
                            <td className="small" style={{ whiteSpace: 'normal', paddingLeft: l.group === 'info' ? 22 : undefined }}>{l.label}{l.group === 'channel_discount' && <span className="muted"> · pago pelo canal</span>}</td>
                            <td className="num">{money(l.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </section>
                  <section className="stack-y" style={{ gap: 6 }}>
                    <h3 style={{ margin: 0 }}>Resultado</h3>
                    <table className="grid" style={{ width: '100%' }}>
                      <tbody>
                        <tr><td>Líquido do canal</td><td className="num">{brl(o.totals.net)}</td></tr>
                        <tr><td>− Custo dos produtos</td><td className="num">{money(-o.totals.cost)}</td></tr>
                        {o.expenses.map((e) => <tr key={e.id}><td style={{ whiteSpace: 'normal' }}>− {e.name} <span className="small muted">· despesa</span></td><td className="num">{money(-e.amount)}</td></tr>)}
                        <tr style={{ fontWeight: 600, background: 'var(--surface-2)' }}><td>= Resultado</td><td className="num">{o.totals.result == null ? '—' : money(o.totals.result)}</td></tr>
                      </tbody>
                    </table>
                  </section>
                </div>
              )}
              <section className="stack-y" style={{ gap: 6 }}>
                <h3 style={{ margin: 0 }}>Itens</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table className="grid" style={{ width: '100%' }}>
                    <thead><tr><th>Produto</th><th className="num">Qtd.</th><th className="num">Vendido</th><th className="num">Taxas</th><th className="num">Frete</th><th className="num">Líquido</th><th className="num">Custo</th><th className="num">Sobra</th></tr></thead>
                    <tbody>
                      {o.items.map((i) => {
                        const cost = i.unitCost == null ? null : i.unitCost * i.quantity;
                        return (
                          <tr key={i.id}>
                            <td style={{ whiteSpace: 'normal', minWidth: 180 }}>{i.title}<div className="small muted">{i.sku ?? 'sem SKU'}</div></td>
                            <td className="num">{i.quantity}</td>
                            <td className="num">{brl(i.gross - i.sellerDiscount)}</td>
                            <td className="num">{brl(i.fees)}</td>
                            <td className="num">{brl(i.shipping)}</td>
                            <td className="num">{brl(i.net)}</td>
                            <td className="num">{cost == null ? <Tag tone="warn">Sem custo</Tag> : brl(cost)}{i.costSource === 'current' && <div className="small muted">custo atual</div>}</td>
                            <td className="num">{cost == null ? '—' : money(i.net - cost)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="small muted" style={{ margin: 0 }}>"Sobra" é antes das despesas. Taxas e frete do pedido são divididos entre os itens pelo valor vendido de cada um.</p>
              </section>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------- Resultados

interface ProductRow {
  key: string; sku: string | null; title: string; brand: string | null; category: string | null; orders: number; units: number; sold: number;
  channelDiscount: number; fees: number; shipping: number; net: number; cost: number; expenses: number; result: number; margin: number | null; noCostUnits: number;
}

export function FinanceResults() {
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Resultados</h1>
          <p>O que mais vendeu, o que mais deu lucro e o que deu prejuízo, depois de taxas, frete, custo e despesas.</p>
        </div>
      </div>
      <ResultsItems />
    </>
  );
}

function ResultsItems() {
  const f = useFinanceFilter();
  const group = (f.params.get('por') ?? 'product') as 'product' | 'brand' | 'category';
  const q = new URLSearchParams(f.query);
  q.set('group', group);
  const data = useQuery({ queryKey: ['finance-products', q.toString()], queryFn: () => api<{ items: ProductRow[] }>(`/finance/products?${q}`), placeholderData: keepPreviousData });
  const accounts = useQuery({ queryKey: ['finance-accounts'], queryFn: () => api<Account[]>('/finance/accounts') });
  const [sort, setSort] = useState<keyof ProductRow>('result');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const items = data.data?.items ?? [];
  const tot = items.reduce((a, i) => ({ sold: a.sold + i.sold, result: a.result + i.result, units: a.units + i.units }), { sold: 0, result: 0, units: 0 });
  const top = (key: (i: ProductRow) => number, filter: (i: ProductRow) => boolean = () => true, asc = false) =>
    [...items].filter(filter).sort((a, b) => (asc ? key(a) - key(b) : key(b) - key(a))).slice(0, 100);
  const minSold = tot.sold / Math.max(items.length, 1) / 2; // margem baixa: só itens com venda relevante
  const label = group === 'product' ? 'Produto' : group === 'brand' ? 'Marca' : 'Categoria';
  const sorted = [...items].sort((a, b) => {
    const x = a[sort] ?? -Infinity, y = b[sort] ?? -Infinity;
    return (typeof x === 'string' ? String(x).localeCompare(String(y)) : (x as number) - (y as number)) * (dir === 'asc' ? 1 : -1);
  });
  const th = (k: keyof ProductRow, l: string, numeric = true) => (
    <th className={numeric ? 'num' : undefined} style={{ cursor: 'pointer' }} onClick={() => { if (sort === k) setDir(dir === 'asc' ? 'desc' : 'asc'); else { setSort(k); setDir('desc'); } f.set({ pagina: null }); }}>
      {l}{sort === k ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  const page = Number(f.params.get('pagina') ?? 1);
  const PAGE = 50;
  return (
    <>
      <FilterBar f={f} accounts={accounts.data ?? []} extra={(
        <div className="seg" role="group" aria-label="Agrupar por">
          {([['product', 'Produto'], ['brand', 'Marca'], ['category', 'Categoria']] as const).map(([k, l]) => (
            <button key={k} type="button" aria-pressed={group === k} onClick={() => f.set({ por: k === 'product' ? null : k })}>{l}</button>
          ))}
        </div>
      )} />
      {data.error ? <ErrorBox error={data.error} /> : data.isPending ? <Loading /> : items.length === 0 ? (
        <div className="panel empty"><b>Sem vendas com extrato no período</b>Mude o período ou aguarde a leitura dos extratos.</div>
      ) : (
        <div className="stack-y">
          <div className="kpis">
            <div className="kpi"><span className="k-label">Vendido</span><span className="k-value">{brl(tot.sold)}</span><span className="k-note">{int(tot.units)} unidades · {int(items.length)} {label.toLowerCase()}s</span></div>
            <div className={`kpi${tot.result < 0 ? ' alert' : ''}`}><span className="k-label">Resultado</span><span className="k-value">{brl(tot.result)}</span><span className="k-note">margem {share(tot.result, tot.sold) || '—'}</span></div>
            <div className="kpi"><span className="k-label">Com prejuízo</span><span className="k-value">{int(items.filter((i) => i.result < 0).length)}</span><span className="k-note">{brl(items.filter((i) => i.result < 0).reduce((a, i) => a + i.result, 0))} no período</span></div>
            <div className="kpi"><span className="k-label">Sem custo</span><span className="k-value">{int(items.filter((i) => i.noCostUnits > 0).length)}</span><span className="k-note">resultado superestimado</span></div>
          </div>
          <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))', gap: 'var(--gap)' }}>
            <Ranking label={label} title="Mais vendidos" note="por valor vendido" rows={top((i) => i.sold)} value={(i) => brl(i.sold)} sub={(i) => `${int(i.units)} un. · resultado ${brl(i.result)}`} />
            <Ranking label={label} title="Mais lucro" note="resultado no período" rows={top((i) => i.result, (i) => i.result > 0)} value={(i) => brl(i.result)} sub={(i) => `margem ${pctOf(i.margin)} · ${int(i.units)} un.`} />
            <Ranking label={label} title="Mais prejuízo" note="resultado negativo" tone="err" rows={top((i) => i.result, (i) => i.result < 0, true)} value={(i) => money(i.result)} sub={(i) => `margem ${pctOf(i.margin)} · ${int(i.units)} un.`} />
            <Ranking label={label} title="Menor margem" note="entre os que vendem bem" rows={top((i) => i.margin ?? 0, (i) => i.margin != null && i.sold >= minSold, true)} value={(i) => pctOf(i.margin)} sub={(i) => `vendido ${brl(i.sold)} · resultado ${brl(i.result)}`} />
          </div>
          <section className="panel flush">
            <div style={{ overflowX: 'auto' }}>
              <table className="grid" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    {th('title', label, false)}{th('units', 'Unid.')}{th('sold', 'Vendido')}{th('fees', 'Taxas')}{th('shipping', 'Frete')}{th('net', 'Líquido')}
                    {th('cost', 'Custo')}{th('expenses', 'Despesas')}{th('result', 'Resultado')}{th('margin', 'Margem')}
                  </tr>
                </thead>
                <tbody>
                  {sorted.slice((page - 1) * PAGE, page * PAGE).map((i) => (
                    <tr key={i.key}>
                      <td>{i.title}{group === 'product' && <div className="small muted">{[i.sku, i.brand].filter(Boolean).join(' · ')}</div>}{i.noCostUnits > 0 && <Tag tone="warn">Sem custo</Tag>}</td>
                      <td className="num">{int(i.units)}</td>
                      <td className="num">{brl(i.sold)}</td>
                      <td className="num">{brl(i.fees)}</td>
                      <td className="num">{brl(i.shipping)}</td>
                      <td className="num">{brl(i.net)}</td>
                      <td className="num">{brl(i.cost)}</td>
                      <td className="num">{brl(i.expenses)}</td>
                      <td className="num"><b>{money(i.result)}</b></td>
                      <td className="num">{pctOf(i.margin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={page} pageSize={PAGE} total={sorted.length} noun="itens" go={(p) => f.set({ pagina: p > 1 ? String(p) : null })} />
          </section>
        </div>
      )}
    </>
  );
}

/** Quadro com o top 100 (rolagem) e exportação desse top 100 com todas as colunas. */
function Ranking({ title, note, rows, value, sub, tone, label }: { title: string; note: string; rows: ProductRow[]; value: (i: ProductRow) => ReactNode; sub: (i: ProductRow) => string; tone?: string; label: string }) {
  const exportRows = () => downloadCsv(`resultados_${title.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '_')}`,
    ['Posição', label, 'SKU', 'Marca', 'Unid.', 'Vendido', 'Taxas', 'Frete', 'Líquido', 'Custo', 'Despesas', 'Resultado', 'Margem %'],
    rows.map((i, n) => [n + 1, i.title, i.sku, i.brand, i.units, i.sold, i.fees, i.shipping, i.net, i.cost, i.expenses, i.result, i.margin]));
  return (
    <section className="panel board">
      <div className="board-head">
        <div style={{ minWidth: 0 }}><h2 style={{ margin: 0, color: tone ? `var(--${tone})` : undefined }}>{title}</h2><span className="small muted">{note}{rows.length ? ` · top ${rows.length}` : ''}</span></div>
        {rows.length > 0 && (
          <button className="btn sm" style={{ whiteSpace: 'nowrap', flexShrink: 0 }} type="button" onClick={exportRows} title="Baixa este top em planilha (CSV)">
            <Download size={14} /> Exportar
          </button>
        )}
      </div>
      {rows.length === 0 ? <span className="small muted">Nenhum no período.</span> : (
        <ol className="board-list" style={{ gap: 6 }}>
          {rows.map((i) => (
            <li key={i.key} style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', minWidth: 0 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }} title={i.title}>{i.title}</span>
                <b style={{ whiteSpace: 'nowrap' }}>{value(i)}</b>
              </div>
              <div className="small muted">{i.sku ? `${i.sku} · ` : ''}{sub(i)}</div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Despesas

interface ExpenseRow { id: string; name: string; kind: Kind; value: number; channel: string | null; accountId: string | null; validFrom: string; validTo: string | null }
type Kind = 'pct_sale' | 'pct_net' | 'per_order' | 'per_unit' | 'period';
const KIND: Record<Kind, { label: string; unit: string; hint: string }> = {
  pct_sale: { label: '% do valor vendido', unit: '%', hint: 'Ex.: MDR do cartão, imposto sobre a venda que você queira considerar.' },
  pct_net: { label: '% do líquido recebido', unit: '%', hint: 'Ex.: antecipação de recebíveis.' },
  per_order: { label: 'R$ por pedido', unit: 'R$', hint: 'Ex.: embalagem, etiqueta, mão de obra de separação.' },
  per_unit: { label: 'R$ por unidade vendida', unit: 'R$', hint: 'Ex.: brinde, sachê, saco plástico por item.' },
  period: { label: 'R$ no período', unit: 'R$', hint: 'Ex.: Ads do mês, mensalidade, ERP. Dividido entre os pedidos do período pelo valor vendido.' },
};

export function FinanceExpenses() {
  const admin = can(useMe().tenant.access, 'finance', 'write');
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['finance-expenses'], queryFn: () => api<ExpenseRow[]>('/finance/expenses') });
  const accounts = useQuery({ queryKey: ['finance-accounts'], queryFn: () => api<Account[]>('/finance/accounts') });
  const del = useMutation({
    mutationFn: (id: string) => api(`/finance/expenses/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries(),
  });
  const [editing, setEditing] = useState<ExpenseRow | 'new' | null>(null);
  const accs = accounts.data ?? [];
  const scope = (e: ExpenseRow) => e.accountId ? (() => { const a = accs.find((x) => x.id === e.accountId); return a ? `${CHANNEL[a.channel] ?? a.channel} · ${a.name}` : 'uma conta'; })()
    : e.channel ? CHANNEL[e.channel] ?? e.channel : 'Todos os canais';
  const fmtDay = (d: string) => d.split('-').reverse().join('/');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Despesas</h1>
          <p>Custos que os canais não informam e que você quer ver no resultado: MDR, antecipação, embalagem, Ads, mensalidades. Cada despesa vale a partir de uma data (e até uma data, se tiver fim).</p>
        </div>
        {admin && <button className="btn primary" type="button" onClick={() => setEditing('new')}>Nova despesa</button>}
      </div>
      {list.error ? <ErrorBox error={list.error} /> : list.isPending ? <Loading /> : list.data.length === 0 ? (
        <div className="panel empty"><b>Nenhuma despesa lançada</b>O resultado considera só o que os canais informam e o custo dos produtos.</div>
      ) : (
        <section className="panel flush" style={{ overflowX: 'auto' }}>
          <table className="grid" style={{ width: '100%' }}>
            <thead><tr><th>Despesa</th><th>Como entra</th><th className="num">Valor</th><th>Canal</th><th>Vigência</th><th /></tr></thead>
            <tbody>
              {list.data.map((e) => (
                <tr key={e.id}>
                  <td><b>{e.name}</b></td>
                  <td className="small">{KIND[e.kind].label}</td>
                  <td className="num">{KIND[e.kind].unit === '%' ? `${e.value.toLocaleString('pt-BR')}%` : brl(e.value)}</td>
                  <td className="small">{scope(e)}</td>
                  <td className="small">{fmtDay(e.validFrom)} {e.validTo ? `até ${fmtDay(e.validTo)}` : 'em diante'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {admin && <>
                      <button className="icon-btn" type="button" aria-label="Editar" onClick={() => setEditing(e)}><Pencil size={15} /></button>
                      <button className="icon-btn" type="button" aria-label="Excluir" onClick={() => window.confirm(`Excluir "${e.name}"? O resultado dos períodos em que ela valia muda.`) && del.mutate(e.id)}><Trash2 size={15} /></button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {del.error && <ErrorBox error={del.error} />}
      <p className="small muted">Ads: lance o valor gasto no mês como "R$ no período" (de 01 a 31). Na próxima etapa a Berytus lê o gasto de Ads direto da Shopee e do Mercado Livre.</p>
      {editing && <ExpenseDialog expense={editing === 'new' ? null : editing} accounts={accs} onClose={() => setEditing(null)} />}
    </>
  );
}

function ExpenseDialog({ expense, accounts, onClose }: { expense: ExpenseRow | null; accounts: Account[]; onClose: () => void }) {
  const qc = useQueryClient();
  const today = ymd(new Date());
  const [f, setF] = useState({
    name: expense?.name ?? '', kind: (expense?.kind ?? 'pct_sale') as Kind, value: expense ? String(expense.value).replace('.', ',') : '',
    channel: expense?.channel ?? '', accountId: expense?.accountId ?? '', validFrom: expense?.validFrom ?? `${today.slice(0, 8)}01`, validTo: expense?.validTo ?? '',
  });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name, kind: f.kind, value: Number(f.value.replace(/\./g, '').replace(',', '.')), channel: f.channel || null, accountId: f.accountId || null,
        validFrom: f.validFrom, validTo: f.validTo || null,
      };
      return expense ? api(`/finance/expenses/${expense.id}`, { method: 'PUT', json: body }) : api('/finance/expenses', { method: 'POST', json: body });
    },
    onSuccess: () => { qc.invalidateQueries(); onClose(); },
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" aria-describedby={undefined}
          style={{ position: 'fixed', top: '8vh', left: '50%', transform: 'translateX(-50%)', width: 'min(560px, calc(100vw - 32px))', maxHeight: '84vh', overflow: 'auto', zIndex: 60 }}>
          <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Dialog.Title asChild><h2 style={{ margin: 0 }}>{expense ? 'Editar despesa' : 'Nova despesa'}</h2></Dialog.Title>
              <span style={{ flex: 1 }} />
              <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
            </div>
            <div className="field"><label htmlFor="ex-name">Nome</label><input className="input" id="ex-name" required autoFocus value={f.name} onChange={set('name')} placeholder="MDR cartão, Embalagem, Ads Shopee…" /></div>
            <div className="field">
              <label htmlFor="ex-kind">Como entra no resultado</label>
              <select className="select" id="ex-kind" value={f.kind} onChange={set('kind')}>
                {(Object.keys(KIND) as Kind[]).map((k) => <option key={k} value={k}>{KIND[k].label}</option>)}
              </select>
              <span className="small muted">{KIND[f.kind].hint}</span>
            </div>
            <div className="field"><label htmlFor="ex-value">Valor ({KIND[f.kind].unit})</label><input className="input" id="ex-value" required inputMode="decimal" value={f.value} onChange={set('value')} style={{ maxWidth: 200 }} /></div>
            <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <div className="field">
                <label htmlFor="ex-ch">Canal</label>
                <select className="select" id="ex-ch" value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value, accountId: '' })}>
                  <option value="">Todos os canais</option>
                  {[...new Set(accounts.map((a) => a.channel))].map((c) => <option key={c} value={c}>{CHANNEL[c] ?? c}</option>)}
                </select>
              </div>
              {f.channel && accounts.filter((a) => a.channel === f.channel).length > 1 && (
                <div className="field">
                  <label htmlFor="ex-acc">Conta / loja</label>
                  <select className="select" id="ex-acc" value={f.accountId} onChange={set('accountId')}>
                    <option value="">Todas</option>
                    {accounts.filter((a) => a.channel === f.channel).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="cols" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <div className="field"><label htmlFor="ex-from">Vale a partir de</label><input className="input" id="ex-from" type="date" required value={f.validFrom} onChange={set('validFrom')} /></div>
              <div className="field"><label htmlFor="ex-to">Até {f.kind === 'period' ? '' : '(opcional)'}</label><input className="input" id="ex-to" type="date" required={f.kind === 'period'} value={f.validTo} onChange={set('validTo')} /></div>
            </div>
            {save.error && <ErrorBox error={save.error} />}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Salvando…' : 'Salvar'}</button>
              <button className="btn" type="button" onClick={onClose}>Cancelar</button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------- Repasses

interface PayoutsResponse {
  days: { date: string; accountId: string; orders: number; payout: number; net: number; diff: number; diverging: number }[];
  pending: { orders: number; net: number };
  wallet: { id: string; accountId: string; type: string; status: string | null; amount: number; orderSn: string | null; description: string | null; at: string }[];
}
const WALLET: Record<string, string> = {
  ESCROW_VERIFIED_ADD: 'Repasse de pedido', ESCROW_VERIFIED_MINUS: 'Pedido com saldo negativo', WITHDRAWAL_CREATED: 'Saque solicitado',
  WITHDRAWAL_COMPLETED: 'Saque concluído', WITHDRAWAL_CANCELLED: 'Saque cancelado', ADJUSTMENT_ADD: 'Ajuste a favor', ADJUSTMENT_MINUS: 'Ajuste cobrado',
  ADJUSTMENT_CENTER_ADD: 'Ajuste a favor', ADJUSTMENT_CENTER_DEDUCT: 'Ajuste cobrado', PAID_ADS_CHARGE: 'Ads (cobrança)', PAID_ADS_REFUND: 'Ads (estorno)',
  AFFILIATE_ADS_SELLER_FEE: 'Ads de afiliados', AFFILIATE_ADS_SELLER_FEE_REFUND: 'Ads de afiliados (estorno)', AFFILIATE_FEE_DEDUCT: 'Taxa de afiliados',
  FAST_ESCROW_DISBURSE: 'Repasse antecipado', FAST_ESCROW_DISBURSE_REMAIN: 'Repasse antecipado (restante)', FAST_ESCROW_DEDUCT: 'Repasse antecipado (devolução)',
  FSF_COST_PASSING_DEDUCT: 'Frete de pedido cancelado',
};

export function FinancePayouts() {
  const f = useFinanceFilter();
  const q = useQuery({ queryKey: ['finance-payouts', f.query.toString()], queryFn: () => api<PayoutsResponse>(`/finance/payouts?${f.query}`), placeholderData: keepPreviousData });
  const accounts = useQuery({ queryKey: ['finance-accounts'], queryFn: () => api<Account[]>('/finance/accounts') });
  const [open, setOpen] = useState<string | null>(null);
  const [showOrders, setShowOrders] = useState(false);
  const d = q.data;
  const accName = (id: string) => accounts.data?.find((a) => a.id === id)?.name ?? '';
  const tot = (d?.days ?? []).reduce((a, x) => ({ payout: a.payout + x.payout, net: a.net + x.net, orders: a.orders + x.orders, diverging: a.diverging + x.diverging }), { payout: 0, net: 0, orders: 0, diverging: 0 });
  const wallet = (d?.wallet ?? []).filter((w) => showOrders || !['ESCROW_VERIFIED_ADD', '101'].includes(w.type));
  const ads = (d?.wallet ?? []).filter((w) => /ADS|AFFILIATE|^45[0-9]|^460$/.test(w.type)).reduce((a, w) => a - w.amount, 0);
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Repasses</h1>
          <p>O que a Shopee liberou para a loja em cada dia, pedido a pedido, contra o líquido previsto no extrato; e as movimentações da carteira (Ads, ajustes, saques). Mercado Livre na próxima etapa.</p>
        </div>
      </div>
      <FilterBar f={f} accounts={(accounts.data ?? []).filter((a) => a.channel === 'shopee')} />
      {q.error ? <ErrorBox error={q.error} /> : !d ? <Loading /> : (
        <div className="stack-y">
          <div className="kpis">
            <div className="kpi"><span className="k-label">Repassado no período</span><span className="k-value">{brl(tot.payout)}</span><span className="k-note">{int(tot.orders)} pedidos liberados</span></div>
            <div className={`kpi${Math.abs(tot.payout - tot.net) >= 0.01 ? ' alert' : ''}`}><span className="k-label">Diferença × previsto</span><span className="k-value">{brl(tot.payout - tot.net)}</span><span className="k-note">{int(tot.diverging)} pedido(s) com valor diferente</span></div>
            <div className="kpi"><span className="k-label">A receber</span><span className="k-value">{brl(d.pending.net)}</span><span className="k-note">{int(d.pending.orders)} pedidos ainda não liberados (todo o período)</span></div>
            <div className="kpi"><span className="k-label">Ads cobrado na carteira</span><span className="k-value">{brl(ads)}</span><span className="k-note">já entra como despesa na Conciliação</span></div>
          </div>
          <section className="panel flush" style={{ overflowX: 'auto' }}>
            {d.days.length === 0 ? <div className="empty"><b>Nenhum repasse no período</b>Os repasses são lidos da Shopee a cada hora.</div> : (
              <table className="grid" style={{ width: '100%' }}>
                <thead><tr><th>Dia</th><th>Loja</th><th className="num">Pedidos</th><th className="num">Previsto</th><th className="num">Repassado</th><th className="num">Diferença</th><th /></tr></thead>
                <tbody>
                  {d.days.map((x) => {
                    const k = `${x.date}|${x.accountId}`;
                    return (
                      <PayoutDay key={k} x={x} account={accName(x.accountId)} open={open === k} toggle={() => setOpen(open === k ? null : k)} />
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
          <section className="panel stack-y" style={{ gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0 }}>Carteira da Shopee</h2>
              <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" className="check" checked={showOrders} onChange={(e) => setShowOrders(e.target.checked)} /> Mostrar também os repasses de pedidos
              </label>
            </div>
            {!wallet.length ? <span className="small muted">Nenhuma movimentação no período.</span> : (
              <div style={{ overflowX: 'auto' }}>
                <table className="grid" style={{ width: '100%' }}>
                  <thead><tr><th>Quando</th><th>Loja</th><th>Movimentação</th><th>Pedido</th><th className="num">Valor</th></tr></thead>
                  <tbody>
                    {wallet.slice(0, 300).map((w) => (
                      <tr key={w.id}>
                        <td className="small">{dateTime(w.at)}</td>
                        <td className="small">{accName(w.accountId)}</td>
                        <td style={{ whiteSpace: 'normal' }}>{WALLET[w.type] ?? w.type}{w.description && <div className="small muted">{w.description}</div>}</td>
                        <td className="code small">{w.orderSn ?? ''}</td>
                        <td className="num">{money(w.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}

function PayoutDay({ x, account, open, toggle }: { x: PayoutsResponse['days'][number]; account: string; open: boolean; toggle: () => void }) {
  const q = useQuery({
    queryKey: ['finance-payout-orders', x.date, x.accountId],
    queryFn: () => api<{ id: string; externalId: string; createdAt: string; payout: number; net: number; diff: number }[]>(`/finance/payouts/orders?date=${x.date}&accountId=${x.accountId}`),
    enabled: open,
  });
  return (
    <>
      <tr>
        <td>{x.date.split('-').reverse().join('/')}</td>
        <td className="small">{account}</td>
        <td className="num">{int(x.orders)}</td>
        <td className="num">{brl(x.net)}</td>
        <td className="num"><b>{brl(x.payout)}</b></td>
        <td className="num">{Math.abs(x.diff) >= 0.01 ? <span style={{ color: 'var(--err)' }}>{brl(x.diff)} · {x.diverging}</span> : '—'}</td>
        <td><button className="btn sm quiet" type="button" onClick={toggle}>{open ? 'Fechar' : 'Pedidos'}</button></td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7}>
            {q.isPending ? <Loading /> : q.error ? <ErrorBox error={q.error} /> : (
              <table className="grid" style={{ width: '100%' }}>
                <thead><tr><th>Pedido</th><th>Data da venda</th><th className="num">Previsto</th><th className="num">Repassado</th><th className="num">Diferença</th></tr></thead>
                <tbody>
                  {q.data.map((o) => (
                    <tr key={o.id}>
                      <td className="code">{o.externalId}</td>
                      <td className="small">{dateTime(o.createdAt)}</td>
                      <td className="num">{brl(o.net)}</td>
                      <td className="num">{brl(o.payout)}</td>
                      <td className="num">{Math.abs(o.diff) >= 0.01 ? <span style={{ color: 'var(--err)' }}>{brl(o.diff)}</span> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
